import React, { useState } from "react";
import { Column, Row, Text, Card, Divider, Expander, Icon, Button } from "@amzn/meridian";
import { InspectionResult } from "src/services/inspectionService";
import ThemedTag from "./ThemedTag";
import ComplianceResultRow from "./ComplianceResultRow";
import ComplianceDetailSheet from "./ComplianceDetailSheet";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import "./CheckpointCard.scss";

export interface CheckpointCardProps {
    questionNumber: string;
    questionText: string;
    isAI: boolean;
    allCompliant: boolean;
    results: InspectionResult[];
    presignedUrlMap: Record<string, string>;
}

const CheckpointCard: React.FC<CheckpointCardProps> = ({
    questionNumber,
    questionText,
    isAI,
    results,
    allCompliant,
    presignedUrlMap
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const [selectedResult, setSelectedResult] = useState<InspectionResult | null>(null);
    const [sheetOpen, setSheetOpen] = useState(false);

    const openSheet = (result: InspectionResult) => {
        setSelectedResult(result);
        setSheetOpen(true);
    };

    return (
        <Card spacingInset="none">
            <Column spacingInset="400" spacing="300">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text type="h100" color="primary">
                        {questionNumber}
                    </Text>
                    <Row spacing="200" alignmentVertical="center">
                        {allCompliant ? <ThemedTag text="Compliant" type="success" />
                            : <ThemedTag text="Non Compliant" type="error" />}
                        <ThemedTag text={isAI ? "AI" : "Manual"} type="neutral" />
                    </Row>
                </Row>

                <Text type="b200" color="secondary">
                    {questionText}
                </Text>

                <Divider />

                {results[0].objectId === null ?
                    <Row alignmentHorizontal="justify" alignmentVertical="center" spacingInset="200 none">
                        <Text type="b200" color="secondary">{results[0].confirmedResult}</Text>
                        {results[0].evidence.length > 0 && (
                            <Button onClick={() => openSheet(results[0])} type="icon">
                                <Icon tokens={chevronRightSmallTokens} color="secondary" />
                            </Button>
                        )}
                    </Row> :

                    <Expander
                        title="Detailed compliance status"
                        open={showDetails}
                        onChange={(open: boolean) => setShowDetails(open)}
                        type="inline"
                        spacingInset="none"
                    >
                        <Column spacing="none">
                            {results.map((result, idx) => (
                                <ComplianceResultRow
                                    key={result.resultId}
                                    result={result}
                                    index={idx}
                                    onClick={() => openSheet(result)}
                                />
                            ))}
                        </Column>
                    </Expander>}
            </Column>

            <ComplianceDetailSheet
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
                result={selectedResult}
                presignedUrlMap={presignedUrlMap}
            />
        </Card>
    );
};

export default CheckpointCard;
